import { useEffect, useRef, FC } from 'react'
import styled from 'styled-components'
import { RegisterOptions, useFormContext } from 'react-hook-form'

import { useGoogleMapsScriptReady } from 'hooks/useGoogleMapsScriptLoadedRef'
import { TextFieldWrapper } from 'components/form'

const InputWrapper = styled.div<{ error?: boolean; rounded?: boolean }>`
  input {
    width: 100%;
    padding: 18px;
    margin: 8px 0 8px;
    outline: 0;
    color: ${({ theme }) => theme.colors.labelGrey};
    text-overflow: ellipsis;
    border: 1px solid
      ${({ theme, error }) =>
        error ? theme?.['-ds-error-300']?.value : theme.colors.textGreyLight};

    ${({ rounded }) =>
      rounded &&
      `
      border-radius: 6px;
    `}

    &:focus {
      border-color: ${({ theme }) => theme.brand.jungle};
    }
  }
`

export type SelectedPlace = {
  location: string | null
  latitude: number | null
  longitude: number | null
  googlePlaceId: string | null
}

type Props = {
  name: string
  onSelect: (place: SelectedPlace) => void
  rules?: RegisterOptions
  error?: boolean
  rounded?: boolean
  placeholder?: string
}

const AuthPlacesInput: FC<Props> = ({
  name,
  onSelect,
  rules,
  error,
  rounded = true,
  placeholder = 'Enter your location (city, country)',
}) => {
  const { register } = useFormContext()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const googleReady = useGoogleMapsScriptReady()
  const instantiated = useRef(false)
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  const { ref: registerRef, ...rest } = register(name, rules)

  useEffect(() => {
    if (!inputRef.current || !googleReady || instantiated.current) {
      return undefined
    }
    // @ts-ignore -- google maps types
    const { Autocomplete } = google.maps.places
    const instance = new Autocomplete(inputRef.current, {
      types: ['(regions)'],
    })
    const listener = instance.addListener('place_changed', () => {
      const place = instance.getPlace()
      if (!place?.geometry) {
        onSelectRef.current({
          location: null,
          latitude: null,
          longitude: null,
          googlePlaceId: null,
        })
        return
      }
      onSelectRef.current({
        location: place.formatted_address || null,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
        googlePlaceId: place.place_id || null,
      })
    })
    setTimeout(
      () => inputRef.current?.setAttribute('autocomplete', 'new-location'),
      200,
    )
    instantiated.current = true
    return () => {
      // @ts-ignore -- google maps types
      google.maps.event.removeListener(listener)
    }
  }, [googleReady])

  return (
    <TextFieldWrapper>
      <InputWrapper error={error} rounded={rounded}>
        <input
          {...rest}
          id={name}
          name={name}
          type="text"
          placeholder={placeholder}
          autoComplete="off"
          ref={(e) => {
            registerRef(e)
            inputRef.current = e
          }}
        />
      </InputWrapper>
    </TextFieldWrapper>
  )
}

export default AuthPlacesInput
